import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import clsx from 'clsx';
import { Modal } from './ui/Modal';
import { client } from '../services/apiClient';

interface AddLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function AddLinkModal({ isOpen, onClose }: AddLinkModalProps) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setError(null);
    }
  }, [isOpen]);

  // mutation for adding bookmark
  const addBookmarkMutation = useMutation({
    mutationFn: (url: string) => client.addBookmark(url),
    onSuccess: async () => {
      // Invalidate bookmark caches before closing.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bookmarks'] }),
        queryClient.invalidateQueries({ queryKey: ['currentUser'] }),
      ]);
      onClose();
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // basic URL validation
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      setError(t`Please enter a valid URL.`);
      return;
    }

    // add protocol if missing
    let processedUrl = trimmedUrl;
    if (!trimmedUrl.match(/^https?:\/\//i)) {
      processedUrl = `https://${trimmedUrl}`;
    }

    try {
      new URL(processedUrl);
    } catch {
      setError(t`Please enter a valid URL.`);
      return;
    }

    addBookmarkMutation.mutate(processedUrl);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    // clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  return (
    <Modal.Root
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={t`Add link`}
      initialFocusRef={inputRef}
      size="xl"
    >
      <Modal.Header title={t`Add link`} />
      <Modal.Body>
        <form onSubmit={handleSubmit} className="flex flex-row gap-4">
          <input
            ref={inputRef}
            type="text"
            name="url"
            aria-label={t`URL`}
            value={url}
            onChange={handleInputChange}
            placeholder="https://www.example.com/article.html"
            className={clsx('form-input', error && 'form-input-error')}
            disabled={addBookmarkMutation.isPending}
          />
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={addBookmarkMutation.isPending}
            className={clsx(
              'button-primary px-12 py-4',
              addBookmarkMutation.isPending && 'bg-surface-secondary',
            )}
          >
            {addBookmarkMutation.isPending ? (
              <span className="flex items-center">
                <svg
                  className="mr-2 h-4 w-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <Trans>Adding...</Trans>
              </span>
            ) : (
              <Trans>Add</Trans>
            )}
          </button>
        </form>
      </Modal.Body>
    </Modal.Root>
  );
}

export default AddLinkModal;
